import { ReactNode, createContext, useContext } from 'react';
import { View, Text, Pressable } from 'react-native';
import { ChevronLeft } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';

/**
 * The desktop return slot, in its own module on purpose.
 *
 * `AppLayout` owns the web shell and `ScreenFrame` owns the phone header; this
 * is the one thing they must agree on, so it lives with neither. Putting it in
 * AppLayout would make every consumer of ScreenFrame — including its unit test —
 * pull in WebAppShell, the sidebar, the Supabase client and the submissions
 * query, none of which a header component needs.
 *
 * WHY IT EXISTS AT ALL. `ScreenFrame` draws its back/exit control only on a
 * phone shell, because above the tablet breakpoint `WebAppShell` already draws
 * the title and breadcrumbs and two headers for one page is its own defect. But
 * the sidebar offers navigation ELSEWHERE — it is not a return to this flow's
 * parent, and for the valuation wizard it is not the guarded exit either, so a
 * desktop user could abandon a dirty wizard with no prompt. Registering the
 * return here lets the shell render it while `ScreenFrame` keeps ONE definition
 * of how you leave a screen: the same `handleBack` runs on both shells, so the
 * unsaved-changes prompt behaves identically.
 *
 * A DESCRIPTOR, NOT A NODE. Registering a ReactNode would hand over a new
 * element identity every render and loop. This is plain data, and `run` is read
 * through a ref by the registrant.
 */
export interface DesktopReturn {
  label: string;
  run: () => void;
}

export const DesktopReturnContext = createContext<{
  setReturn: (value: DesktopReturn | null) => void;
} | null>(null);

/** Null outside an AppLayout — callers no-op rather than crash. */
export function useDesktopReturnSlot() {
  return useContext(DesktopReturnContext);
}

export function DesktopReturnButton({ value }: { value: DesktopReturn }) {
  const colors = useThemeColors();
  return (
    <View
      style={{
        height: 44,
        borderRadius: 22,
        overflow: 'hidden',
        backgroundColor: colors.surface.secondary,
      }}
    >
      <Pressable
        onPress={value.run}
        accessibilityRole="button"
        accessibilityLabel={value.label}
        style={{
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          paddingHorizontal: 14,
        }}
      >
        <ChevronLeft size={18} color={colors.text.primary} />
        <Text
          numberOfLines={1}
          className="text-sm font-semibold"
          style={{ color: colors.text.primary }}
        >
          {value.label}
        </Text>
      </Pressable>
    </View>
  );
}

export type { ReactNode };
